import { RiseOutlined } from '@ant-design/icons';
import { useResolutionInfo } from '@mm/hooks';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { MOBILE_SMALL } from '../../../../editor/constants/screenSize';

import styles from './LevelMeter.module.css';

export const calculateCurrentSegmentStop = (
  currentValue: number,
  maxThresholdValue: number,
) => {
  if (currentValue < maxThresholdValue) {
    return currentValue / maxThresholdValue;
  }
  return currentValue / (currentValue + currentValue * 0.13);
};

export const LevelMeter = ({
  currentValue,
  maxThresholdValue,
  className,
  showLogo = true,
  isVertical,
  topLabel,
  bottomLabel,
  css,
}: {
  currentValue: number;
  maxThresholdValue: number;
  className?: string;
  showLogo?: boolean;
  isVertical?: boolean;
  topLabel?: string;
  bottomLabel?: string;
  css?: {
    bottomLabel?: string;
  };
}) => {
  const segments = [
    {
      color: '#ED3C3C',
      label: 'Poor',
      rangeMaxThreshold: 1.0,
      rangeMinThreshold: 0.75,
    },
    {
      color: '#F29324',
      label: 'Low',
      rangeMaxThreshold: 0.75,
      rangeMinThreshold: 0.5,
    },
    {
      color: '#EFC75E',
      label: 'Medium',
      rangeMaxThreshold: 0.5,
      rangeMinThreshold: 0.25,
    },
    {
      color: '#3DB39E',
      label: 'Good',
      rangeMaxThreshold: 0.25,
      rangeMinThreshold: 0.0,
    },
  ];
  const [sliderPosition, setSliderPosition] = useState(0);

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_SMALL;

  useEffect(() => {
    setSliderPosition(
      calculateCurrentSegmentStop(currentValue, maxThresholdValue),
    );
  }, [currentValue, maxThresholdValue]);

  const showLabel = (currentVal: number) => {
    const segment = segments.find(
      segment =>
        currentVal >= segment.rangeMinThreshold &&
        currentVal < segment.rangeMaxThreshold,
    );

    if (segment) {
      return {
        label: segment.label,
        color: segment.color,
      };
    }
  };

  return (
    <div className={classNames(styles.wrapper, className)}>
      <div
        className={classNames(styles.container, {
          [styles.columnReverse]: isVertical,
        })}
      >
        {topLabel && <div className={styles.topLabel}>{topLabel}</div>}
        <div
          className={isVertical ? styles.rangeWrapper : ''}
          style={{
            top: isVertical ? `${sliderPosition * 100}%` : '',
          }}
        >
          <div
            className={classNames(styles.rangeSlider, {
              [styles.rangeSliderVertical]: isVertical,
            })}
            style={{
              right: !isVertical ? `${sliderPosition * 100}%` : '',
            }}
          >
            <div className={styles.sliderTriangle} />
            {isMobile && !isVertical && (
              <div
                className={styles.label}
                style={{ color: showLabel(sliderPosition)?.color }}
              >
                {showLabel(sliderPosition)?.label}
              </div>
            )}
          </div>
          {isVertical && (
            <div
              className={styles.label}
              style={{ color: showLabel(sliderPosition)?.color }}
            >
              {showLabel(sliderPosition)?.label}
            </div>
          )}
        </div>
        {segments.map(segment => {
          return (
            <div
              key={segment.label}
              className={isVertical ? styles.segmentVertical : styles.segment}
              style={{ backgroundColor: segment.color }}
            />
          );
        })}
      </div>
      {bottomLabel && (
        <div className={classNames(styles.label, css?.bottomLabel)}>
          Missing 20% of potential leads
        </div>
      )}
      {!isVertical && (
        <div className={styles.infoBlock}>
          {!isMobile && (
            <div
              className={styles.label}
              style={{ color: showLabel(sliderPosition)?.color }}
            >
              {showLabel(sliderPosition)?.label}
            </div>
          )}

          {showLogo && (
            <div className={styles.improvement}>
              <RiseOutlined className={styles.riseIcon} />
              Room for Improvement
            </div>
          )}
        </div>
      )}
    </div>
  );
};
